import React from "react";

import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { useCreateDocument } from "@hooks";
import { convertDateToTimestamp } from "@utils";

import { AuxiliaryComponent } from "@constants";

import { collection } from "firebase/firestore";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { Alert, ButtonProps, Text } from "@chakra-ui/react";
import { DB_COLLECTION_NAMES } from "@constants";
import { FirebaseContext } from "@contexts";
import { useAuth, useCustomToast } from "@hooks";
import { getCountFromServer, query, where } from "firebase/firestore";
import { ButtonModal, JsonForm, JsonFormControl } from "@components";

type AddCarrierPlatesModalProps = ButtonProps & {
  itemId: string;
};

// Want to parse text that might have commas within quotation marks
function parse(row: string) {
  let insideQuote = false;
  const entries = [];
  let entry: string[] = [];

  row.split("").forEach(function (character) {
    if (character === '"') {
      insideQuote = !insideQuote;
    } else {
      if (character == "," && !insideQuote) {
        entries.push(entry.join(""));
        entry = [];
      } else {
        entry.push(character);
      }
    }
  });
  entries.push(entry.join(""));
  return entries;
}

const readUploadedFileAsText = (inputFile: Blob) => {
  const temporaryFileReader = new FileReader();

  return new Promise<string>((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    temporaryFileReader.onload = () => {
      resolve(temporaryFileReader.result as string);
    };
    temporaryFileReader.readAsText(inputFile);
  });
};

const AddCarrierPlatesFormFields: JsonFormControl[] = [
  {
    label: "BaseplateInput",
    inputType: "csv",
    fieldName: "csv",
    defaultValue: undefined,
  },
];

export function AddCarrierPlatesModal({
  itemId,
  ...delegated
}: AddCarrierPlatesModalProps) {
  const { user, logout } = useAuth();

  const [error, setError] = React.useState("");
  // The number of documents in the total query.
  const { firestore } = React.useContext(FirebaseContext);

  const tagAddedToast = useCustomToast(() => (
    <Alert status="success">Data from CSV added!</Alert>
  ));

  const tagFailedToast = useCustomToast(() => (
    <Alert status="success">Data from CSV failed!</Alert>
  ));

  const serverFailedToast = useCustomToast(() => (
    <Alert status="success">Unable to get DB info!</Alert>
  ));

  const tagExistsToast = useCustomToast(() => (
    <Alert status="success">Data already exists!</Alert>
  ));

  const queryProblemToast = useCustomToast(() => (
    <Alert status="success">Undefined query</Alert>
  ));

  const moduleCollName = DB_COLLECTION_NAMES.AUXILIARY_COMPONENTS;

  //const store = useStoreModuleStatus();

  const { create } = useCreateDocument();

  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      const userId = user?.firebaseUser?.uid || "";
      const userName = user ? `${user.firstName} ${user.lastName}` : "";
      if (!userId || !userName) {
        console.error("Missing auth information. The user will be logged out.");
        await logout();
        return location.reload();
      }

      const csv = data.csv;

      const fileAsText = readUploadedFileAsText(csv);
      const allLines = (await fileAsText).split(/\r\n|\n/);

      for await (const line of allLines) {
        if (!line) continue;
        const columns = parse(line);
        const serial = columns[0];
        const statusString = columns[2];
        const location = user ? user.location : "All";

        const moduleCollRef = collection(firestore, moduleCollName);

        // Find the number of steps from this module that contain the right text
        // that indicates that this has already been done.
        const queryResult = query(moduleCollRef, where("id", "==", serial));

        const storeRef = doc(firestore, "store", "moduleStatus"); // Reference to the document with ID "SF" in the "cities" collection
        const storeDoc = await getDoc(storeRef);
        const store = storeDoc.data();

        let count = -1;
        try {
          const snapshot = await getCountFromServer(queryResult);
          if (snapshot == undefined) {
            queryProblemToast();
          }
          count = (await snapshot).data().count;
        } catch (ex) {
          alert(ex);
          console.error("CSV upload failed, problem with query", serial);
        }
        if (
          count > 0 &&
          store != undefined &&
          location != "All" &&
          !store.partsByLocation[location]?.includes(serial)
        ) {
          const storeRefAux = doc(firestore, "auxiliary-components", serial); // Reference to the document with ID "SF" in the "cities" collection
          await deleteDoc(storeRefAux);
          count = 0;
        }

        if (count == -1) {
          serverFailedToast();
        } else if (count == 0) {
          setError("");

          const authId = user?.firebaseUser?.uid;
          const name = user ? `${user.firstName} ${user.lastName}` : undefined;
          if (!authId || !name) throw new Error("User not logged in.");
          const registeredOn = convertDateToTimestamp(new Date());
          const status =
            statusString == "good" ||
            statusString == "Good" ||
            statusString == "GOOD" ||
            statusString == "okay" ||
            statusString == "Okay" ||
            statusString == "OKAY"
              ? "OKAY"
              : "DO_NOT_USE";
          const componentType =
            serial.indexOf("2S") >= 0
              ? "Aux.Fixture.2S ModuleCarrier"
              : "Aux.Fixture.PS ModuleCarrier";

          const newAuxComponent = {
            id: serial,
            componentType: componentType,
            componentCategory: (
              componentType.split(".")[1] || ""
            ).toLowerCase(),
            registeredOn,
            registeredBy: { authId, name },
            location: location,
            status: status,
          } as AuxiliaryComponent;

          try {
            create(
              DB_COLLECTION_NAMES.AUXILIARY_COMPONENTS,
              newAuxComponent,
              newAuxComponent.id,
            );
            tagAddedToast();
          } catch (ex) {
            alert(ex);
            tagFailedToast();
            console.error("CSV upload failed", ex);
          }
        } else {
          tagExistsToast();
        }
      }
    },
    [itemId],
  );

  return (
    <ButtonModal
      label="Import Carrier Plate Data"
      title="Import CSV"
      size="sm"
      {...delegated}
    >
      <JsonForm
        fields={AddCarrierPlatesFormFields}
        onSubmit={doSubmit}
        clearAfterSubmit
      />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
